import React from "react"
import {compose} from "redux"
import {useTranslation} from "react-i18next"
import {RouteComponentProps, withRouter} from "react-router"

// Components
import ModernAppBar from "../../../../../components/modern/ModernAppBar"
import ModernStatCards from "../ModernStatCards"
import ModernInProgressBanner from "../ModernInProgressBanner"
import ModernRecordsCard from "../ModernRecordsCard"
import ModernPageKeys from "../ModernPageKeys"

// Hooks
import useAccumulatedRecords from "../useAccumulatedRecords"

// Context
import {useUiSkin} from "../../../../ui-skin/context/UiSkinContext"

// HOCs
import withUser, {WithUserProps} from "../../../../auth/hoc/withUser"
import withWSConnection, {WithWSConnectionProps} from "../../../../ws/hoc/withWSConnection"
import withSpeedAndAmplitude, {WithSpeedAndAmplitudeProps} from "../../../../records/hoc/withSpeedAndAmplitude"
import withDoctorStats, {WithDoctorStatsProps} from "../../../hoc/withDoctorStats"
import withInProgressRecord, {WithInProgressRecordProps} from "../../../hoc/withInProgressRecord"
import withDoctorAvailableRecords, {WithDoctorAvailableRecordsType} from "../../../hoc/withDoctorAvailableRecords"

// Constants
import myInitObject from "../../../../GlobalConstants"
import {AppRoute} from "../../../../routing/constants"

// Models
import {AvailableRecordType} from "../../../models"

// Utils
import Utils from "../../../../../utils/Utils"
import {
    DEFAULT_SORT,
    SortKey,
    SortState,
    TabKey,
    countryNameKey,
    filterAndSortRecords,
    nextSort,
    resultMeta,
} from "../ModernRecordsCard/columns"
import {canOpenRecord, recordActionKind} from "../../../utils/recordActions"

// Styles
import useStyles from "./styles"

type ModernHomePageProps =
  RouteComponentProps
  & WithUserProps
  & WithWSConnectionProps
  & WithSpeedAndAmplitudeProps
  & WithDoctorStatsProps
  & WithInProgressRecordProps
  & WithDoctorAvailableRecordsType

/**
 * Modern dashboard.
 *
 * Composes the same HOCs Classic spreads across HomePage, DoctorStatsContainer,
 * InProgressSection and AvailableRecordTable. No new action or service exists
 * for the Modern skin; the only shared-store addition is the
 * `availableRecordsError` flag (see useAccumulatedRecords), which Classic
 * ignores. Every effect below has a Classic counterpart and is annotated with
 * it.
 *
 * @param props
 */
function ModernHomePage(props: ModernHomePageProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {toggleTheme} = useUiSkin();
    const {
        history,
        user,
        stats,
        fetchStats,
        inProgressRecord,
        inProgressRecordFetching,
        fetchInProgressRecord,
        openAvailableRecordsWSConnectionAsync,
        closeAvailableRecordsWSConnection,
        setMMPerMv,
        setMMPerSecond,
    } = props;

    // Same rule as Classic HomePage: company masters see the activity summary
    // but never the doctor work queue.
    const isDoctor = false === Utils.isCompanyMaster(user);

    const [tab, setTab] = React.useState<TabKey>("all");
    const [query, setQuery] = React.useState("");
    const [sort, setSort] = React.useState<SortState>(DEFAULT_SORT);

    const searchRef = React.useRef<HTMLInputElement>(null);

    const {rows, total, loading, done, error, loadMore, retry, pendingCount, applyPending} =
      useAccumulatedRecords(props, isDoctor, tab);

    // --- data lifecycle (each mirrors one Classic component) ----------------

    // HomePage: without this the available-records list stops live-updating and
    // nothing errors.
    React.useEffect(() => {
        openAvailableRecordsWSConnectionAsync();

        return () => {
            closeAvailableRecordsWSConnection()
        }
    }, [openAvailableRecordsWSConnectionAsync, closeAvailableRecordsWSConnection]);

    // DoctorStatsContainer
    React.useEffect(() => {
        fetchStats()
    }, [fetchStats]);

    // InProgressSection
    React.useEffect(() => {
        fetchInProgressRecord()
    }, [fetchInProgressRecord]);

    // --- navigation ---------------------------------------------------------

    // Both handlers are copied from AvailableRecordTable and are deliberately
    // NOT unified: assign resets the trace speed/gain before the record page
    // mounts, continue instead flags the record page to re-fetch status.

    const handleAssignClick = React.useCallback((id: AvailableRecordType["teleRecordId"]) => {
        setMMPerMv(10);
        setMMPerSecond(25);
        history.push(AppRoute.Record, {id})
    }, [setMMPerMv, setMMPerSecond, history]);

    const handleContinue = React.useCallback((id: AvailableRecordType["teleRecordId"]) => {
        myInitObject.FetchRecordStatus = "true";
        history.push(AppRoute.Record, {id})
    }, [history]);

    const handleOpenRecord = React.useCallback((record: AvailableRecordType) => {
        if (recordActionKind(record) === "continue") {
            handleContinue(record.teleRecordId);

            return
        }

        handleAssignClick(record.teleRecordId)
    }, [handleAssignClick, handleContinue]);

    // InProgressSection pushes the record route with NO location state and does
    // not set FetchRecordStatus. Reproduced exactly.
    const handleContinueInProgress = React.useCallback(() => {
        history.push(AppRoute.Record)
    }, [history]);


    // --- derived rows -------------------------------------------------------

    const resultText = React.useCallback((record: AvailableRecordType) => {
        const meta = resultMeta(record);

        if (meta.title !== null) {
            return meta.title
        }

        return meta.textKey === null ? "" : t(meta.textKey)
    }, [t]);

    const countryText = React.useCallback((record: AvailableRecordType) => {
        const key = countryNameKey(record.folder);

        return key === null ? (record.folder || "") : t(key)
    }, [t]);

    const visible = React.useMemo(() => filterAndSortRecords(rows, {
        query: query,
        sort: sort,
        resultText: resultText,
        countryText: countryText,
    }), [rows, query, sort, resultText, countryText]);

    const topRecord = visible.length > 0 ? visible[0] : null;

    const handleSortChange = React.useCallback((key: SortKey) => {
        setSort(current => nextSort(current, key))
    }, []);

    const handleClearFilters = React.useCallback(() => {
        setQuery("");
        setTab("all")
    }, []);

    // --- keyboard shortcuts (legend lives in ModernPageKeys) ----------------

    React.useEffect(() => {
        function onKeyDown(e: KeyboardEvent) {
            if (typeof e.key !== "string" || e.ctrlKey || e.metaKey || e.altKey) {
                return
            }

            const target = e.target as HTMLElement | null;
            const tagName = target === null ? "" : target.tagName;
            const typing = tagName === "INPUT"
              || tagName === "TEXTAREA"
              || tagName === "SELECT"
              || (target !== null && target.isContentEditable === true);

            /*
             * Enter and Space ACTIVATE a focused control — the browser fires the
             * element's own click after this listener runs. Treating those
             * presses as page shortcuts meant a keyboard user who tabbed to row
             * 4's Review button and pressed Enter opened the TOP record instead
             * (both navigations fire, and RecordPageContainer's fetch effect has
             * an empty dependency array, so only the first one takes effect).
             * The same misfire hit the copy button and the tab buttons.
             */
            const activating = target !== null
              && (e.key === "Enter" || e.key === " ")
              && (
                target.tagName === "BUTTON"
                || target.tagName === "A"
                || target.getAttribute("role") === "button"
                || target.getAttribute("role") === "tab"
              );

            if (activating) {
                return
            }

            if (e.key === "/" && false === typing) {
                e.preventDefault();

                if (searchRef.current !== null) {
                    searchRef.current.focus()
                }

                return
            }

            if (e.key === "Escape" && typing && target !== null) {
                target.blur();

                return
            }

            if (typing) {
                return
            }

            if (e.key === "1") {
                setTab("all")
            } else if (e.key === "2") {
                setTab("today")
            } else if (e.key === "3") {
                setTab("week")
            } else if (e.key === "Enter") {
                if (canOpenRecord(topRecord, inProgressRecord) && topRecord !== null) {
                    handleOpenRecord(topRecord)
                }
            } else if (e.key.toLowerCase() === "d") {
                toggleTheme()
            }
        }

        window.addEventListener("keydown", onKeyDown);

        return () => window.removeEventListener("keydown", onKeyDown)
    }, [topRecord, inProgressRecord, handleOpenRecord, toggleTheme]);

    return (
      <React.Fragment>
          <ModernAppBar/>

          <main className={styles.wrap}>
              <ModernStatCards
                stats={stats}
                active={tab}
                onSelect={setTab}
              />

              <ModernInProgressBanner
                record={inProgressRecord}
                fetching={inProgressRecordFetching}
                onContinue={handleContinueInProgress}
              />

              {isDoctor && (
                <React.Fragment>
                    <ModernRecordsCard
                      rows={visible}
                      loadedCount={rows.length}
                      total={total}
                      loading={loading}
                      done={done}
                      error={error}
                      loadMore={loadMore}
                      retry={retry}
                      pendingCount={pendingCount}
                      onApplyPending={applyPending}
                      tab={tab}
                      onTabChange={setTab}
                      query={query}
                      onQueryChange={setQuery}
                      searchRef={searchRef}
                      sort={sort}
                      onSortChange={handleSortChange}
                      onClearFilters={handleClearFilters}
                      inProgressRecord={inProgressRecord}
                      onOpenRecord={handleOpenRecord}
                    />

                    <ModernPageKeys/>
                </React.Fragment>
              )}
          </main>
      </React.Fragment>
    )
}

export {ModernHomePage}

export default compose(
    withUser,
    withWSConnection,
    withDoctorStats,
    withInProgressRecord,
    withDoctorAvailableRecords,
    withSpeedAndAmplitude,
    withRouter,
)(ModernHomePage) as React.ComponentType<any>
